import type * as React from "react";

type QuestionSubtitleMarkdownProps = {
  markdown: string;
};

type InlineMarker = {
  delimiter: string;
  render: (children: React.ReactNode, key: number) => React.ReactNode;
};

// Keep subtitle styling intentionally narrow. We use `++underline++` because
// `__double underscores__` is already a common markdown bold delimiter.

const INLINE_MARKERS: InlineMarker[] = [
  {
    delimiter: "**",
    render: (children, key) => <strong key={key}>{children}</strong>,
  },
  {
    delimiter: "++",
    render: (children, key) => (
      <span key={key} className="underline">
        {children}
      </span>
    ),
  },
  {
    delimiter: "*",
    render: (children, key) => <em key={key}>{children}</em>,
  },
];

function isAdjacentToDoubleAsterisk(markdown: string, index: number): boolean {
  const hasDoubleAsteriskBefore = index >= 2 && markdown.startsWith("**", index - 2);
  const hasDoubleAsteriskAfter =
    index + 2 < markdown.length && markdown.startsWith("**", index + 1);
  return hasDoubleAsteriskBefore || hasDoubleAsteriskAfter;
}

function isStandaloneAsteriskDelimiter(markdown: string, index: number): boolean {
  return markdown[index] === "*" && markdown[index + 1] !== "*";
}

function isClosingAsteriskDelimiter(
  markdown: string,
  index: number,
  parentDelimiter: string | undefined,
): boolean {
  if (markdown[index] !== "*") {
    return false;
  }

  if (markdown[index + 1] !== "*") {
    return markdown[index - 1] !== "*" || isAdjacentToDoubleAsterisk(markdown, index);
  }

  return parentDelimiter === "**" && markdown.startsWith("**", index + 1);
}

function isOpeningDelimiter(markdown: string, cursor: number, delimiter: string): boolean {
  if (!markdown.startsWith(delimiter, cursor)) {
    return false;
  }

  if (delimiter !== "*") {
    return true;
  }

  return (
    isStandaloneAsteriskDelimiter(markdown, cursor) &&
    (markdown[cursor - 1] !== "*" || isAdjacentToDoubleAsterisk(markdown, cursor))
  );
}

type ParseResult = {
  nodes: React.ReactNode[];
  cursor: number;
  closed: boolean;
};

function parseInlineNodes(
  markdown: string,
  cursor = 0,
  closingDelimiter?: string,
  parentDelimiter?: string,
): ParseResult {
  const nodes: React.ReactNode[] = [];
  let key = 0;
  let nextCursor = cursor;

  while (nextCursor < markdown.length) {
    if (closingDelimiter != null) {
      const isClosingDelimiter =
        closingDelimiter === "*"
          ? isClosingAsteriskDelimiter(markdown, nextCursor, parentDelimiter)
          : markdown.startsWith(closingDelimiter, nextCursor);

      if (isClosingDelimiter) {
        return { nodes, cursor: nextCursor + closingDelimiter.length, closed: true };
      }
    }

    // Line break: the literal two-character sequence `\n` (backslash + n).
    // Authors write `\n` by hand in the upstream question service to force a
    // line break — this is checked before inline markers so it always renders
    // as a break, even when nested inside a `**bold**` or `++underline++` span.
    if (markdown[nextCursor] === "\\" && markdown[nextCursor + 1] === "n") {
      nodes.push(<br key={key} />);
      key += 1;
      nextCursor += 2;
      continue;
    }

    const marker = INLINE_MARKERS.find(({ delimiter }) =>
      isOpeningDelimiter(markdown, nextCursor, delimiter),
    );

    if (marker) {
      const result = parseInlineNodes(
        markdown,
        nextCursor + marker.delimiter.length,
        marker.delimiter,
        closingDelimiter,
      );

      if (result.closed) {
        nodes.push(marker.render(result.nodes, key));
        nextCursor = result.cursor;
        key += 1;
        continue;
      }
    }

    nodes.push(markdown[nextCursor]);
    nextCursor += 1;
  }

  return { nodes, cursor: nextCursor, closed: false };
}

export function QuestionSubtitleMarkdown({ markdown }: QuestionSubtitleMarkdownProps) {
  return <>{parseInlineNodes(markdown).nodes}</>;
}
